/**
 * Frame Utility Functions
 *
 * Handles conversion between seconds (decimal) and frame notation (seconds.frames)
 *
 * In frame notation:
 * - Whole numbers represent seconds
 * - Decimals represent frames (0-fps)
 * - Example at 24fps: 12.23 = 12 seconds + 23 frames
 * - After 12.23 comes 13.0 (rolls over at frame count)
 */

/**
 * Convert seconds (decimal) to frame notation display string
 * @param seconds - Time in seconds (decimal)
 * @param fps - Frames per second
 * @returns Frame notation string (e.g., "12.23")
 */
export function secondsToFrameNotation(seconds: number, fps: number): string {
  const wholeSec = Math.floor(seconds);
  const frames = Math.round((seconds - wholeSec) * fps);

  // Handle frame rollover (e.g., 24 frames becomes next second)
  if (frames >= fps) {
    return `${wholeSec + 1}.0`;
  }

  return `${wholeSec}.${frames}`;
}

/**
 * Convert frame notation string to seconds (decimal)
 * @param notation - Frame notation string (e.g., "12.23")
 * @param fps - Frames per second
 * @returns Time in seconds (decimal)
 */
export function frameNotationToSeconds(notation: string, fps: number): number {
  const parts = notation.split('.');
  const wholeSec = parseInt(parts[0] || '0', 10);
  const frames = parseInt(parts[1] || '0', 10);

  // Clamp frames to valid range
  const clampedFrames = Math.min(frames, fps - 1);

  return wholeSec + (clampedFrames / fps);
}

/**
 * Snap seconds to nearest frame boundary
 * @param seconds - Time in seconds (decimal)
 * @param fps - Frames per second
 * @returns Time snapped to nearest frame
 */
export function snapToFrame(seconds: number, fps: number): number {
  const frameCount = Math.round(seconds * fps);
  return frameCount / fps;
}

/**
 * Format frame notation for display (pad frames with leading zero if needed)
 * @param seconds - Time in seconds (decimal)
 * @param fps - Frames per second
 * @returns Formatted string (e.g., "12.05" for frame 5 at 24fps)
 */
export function formatFrameNotation(seconds: number, fps: number): string {
  const wholeSec = Math.floor(seconds);
  const frames = Math.round((seconds - wholeSec) * fps);

  // Handle frame rollover
  if (frames >= fps) {
    return `${wholeSec + 1}.00`;
  }

  // Pad frames based on fps digit count
  const framesStr = frames.toString().padStart(2, '0');

  return `${wholeSec}.${framesStr}`;
}
